import { useCallback, useMemo, useState } from 'react';

import PersonAddAltIcon from '@mui/icons-material/PersonAdd';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import SearchIcon from '@mui/icons-material/Search';
import type { GridRowId } from '@mui/x-data-grid';
import type { GridInitialState } from '@mui/x-data-grid';

import { ensureDefined } from '@e-socna/utils';
import { ThirdPartyTypeEnum, makeFragmentData } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type {
  GenericStatusEnum,
  ThirdPartyTypeEnum as ThirdPartyTypeEnumType,
} from '@e-socna/data-layer';

import { useActionBlocker } from '../../../../hooks/useActionBlocker.hook';
import {
  ThirdParties,
  ThirdPartiesUseCase,
} from '../../../../components/ThirdParties';
import { SpeedDial } from '../../../../components/SpeedDial';
import { RightDrawer } from '../../../../components/RightDrawer';
import { OrganizationSearchPanel } from '../../../../pages/OrganizationSearch/OrganizationSearchPanel';
import type { SelectionRestriction } from '../../../../components/ThirdParties/types/selection-restriction.type';

import { FormVariant } from '../../../enums/variant.enum';

import { PersonForm } from '../../Person';
import { OrganizationForm } from '../../Organization';
import { ThirdPartyForm_Person_Fragment } from '../../fragments/person.fragment';
import { ThirdPartyForm_Organization_Fragment } from '../../fragments/organization.fragment';
import type { ThirdPartyMutationResult } from '../../types/third-party-mutation-result.type';
import type { ThirdPartyForm_Person } from '../../fragments/person.fragment';
import type { ThirdPartyForm_Organization } from '../../fragments/organization.fragment';

import type { ListOfIdFieldValue } from './types/field-value.type';
import type { DrawerState } from './types/drawer-state.type';
import type { CreationPrefill } from './types/creation-prefill.type';

interface Props {
  readonly cleanFieldValueAsId: readonly GridRowId[];
  readonly creationPrefill?: CreationPrefill;
  readonly excludedIds?: ListOfIdFieldValue;
  readonly initialGridState?: GridInitialState;
  readonly isInternal?: boolean;
  readonly kind?: ThirdPartyTypeEnumType;
  readonly onCreateSuccess?: () => void;
  readonly onSelectionChange: (newSelection: readonly GridRowId[]) => void;
  readonly selectionRestriction?: SelectionRestriction;
  readonly status?: GenericStatusEnum;
  readonly subsetIds?: ListOfIdFieldValue;
}

export const Selection = ({
  cleanFieldValueAsId,
  creationPrefill,
  excludedIds,
  initialGridState,
  isInternal,
  kind,
  onCreateSuccess: handleCreateSuccess,
  onSelectionChange: handleSelectionChange,
  selectionRestriction,
  status,
  subsetIds,
}: Readonly<Props>) => {
  const [drawerState, setDrawerState] = useState<DrawerState>({
    isOpen: false,
    kind: undefined,
    mode: 'create',
  });

  const openPersonDrawer = useCallback(() => {
    setDrawerState({
      isOpen: true,
      kind: ThirdPartyTypeEnum.Person,
      mode: 'create',
    });
  }, []);

  const openOrganizationDrawer = useCallback(() => {
    setDrawerState({
      isOpen: true,
      kind: ThirdPartyTypeEnum.Organization,
      mode: 'create',
    });
  }, []);

  const openOrganizationSearchDrawer = useCallback(() => {
    setDrawerState({
      isOpen: true,
      kind: ThirdPartyTypeEnum.Organization,
      mode: 'search',
    });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerState({ isOpen: false, kind: undefined, mode: 'create' });
  }, []);

  const actions = [
    ...(kind === ThirdPartyTypeEnum.Person || !kind
      ? [
          {
            id: 'createPerson',
            icon: <PersonAddAltIcon />,
            name: 'Créer Personne',
            onClick: openPersonDrawer,
          },
        ]
      : EMPTY_ARRAY),
    ...(kind === ThirdPartyTypeEnum.Organization || !kind
      ? [
          {
            id: 'createOrganization',
            icon: <DomainAddIcon />,
            name: 'Créer Organisation',
            onClick: openOrganizationDrawer,
          },
          {
            id: 'searchOrganization',
            icon: <SearchIcon />,
            name: 'Rechercher une organisation',
            onClick: openOrganizationSearchDrawer,
          },
        ]
      : EMPTY_ARRAY),
  ];

  const handleSuccess = useCallback(
    (result: ThirdPartyMutationResult) => {
      closeDrawer();

      const { id } = ensureDefined({
        id:
          'createOrganization' in result
            ? result.createOrganization._id
            : 'findOneAndUpdateOrganization' in result
              ? result.findOneAndUpdateOrganization._id
              : 'createPerson' in result
                ? result.createPerson._id
                : 'findOneAndUpdatePerson' in result
                  ? result.findOneAndUpdatePerson._id
                  : undefined,
      });

      handleSelectionChange([...cleanFieldValueAsId, id]);

      handleCreateSuccess?.();
    },
    [
      cleanFieldValueAsId,
      closeDrawer,
      handleCreateSuccess,
      handleSelectionChange,
    ],
  );

  const [blockableCloseDrawer, setMustBlockAction] =
    useActionBlocker(closeDrawer);

  const {
    forOrganization: prefillForOrganization,
    forPerson: prefillForPerson,
  } = creationPrefill ?? {};

  const personInitialValues = useMemo(
    () =>
      prefillForPerson
        ? makeFragmentData(
            prefillForPerson as ThirdPartyForm_Person,
            ThirdPartyForm_Person_Fragment,
          )
        : undefined,
    [prefillForPerson],
  );

  const organizationInitialValues = useMemo(
    () =>
      prefillForOrganization
        ? makeFragmentData(
            prefillForOrganization as ThirdPartyForm_Organization,
            ThirdPartyForm_Organization_Fragment,
          )
        : undefined,
    [prefillForOrganization],
  );

  return (
    <>
      <ThirdParties
        excludedIds={excludedIds}
        initialGridState={initialGridState}
        isInternal={isInternal}
        kind={kind}
        selection={cleanFieldValueAsId}
        selectionRestriction={selectionRestriction}
        setSelection={handleSelectionChange}
        status={status}
        subsetIds={subsetIds}
        useCase={ThirdPartiesUseCase.Select}
      />
      <SpeedDial actions={actions} title="Actions possibles sur les Tiers" />
      <RightDrawer
        isOpen={drawerState.isOpen}
        onDrawerClose={blockableCloseDrawer}
        title={
          drawerState.mode === 'search'
            ? 'Rechercher et créer une organisation'
            : undefined
        }
      >
        {drawerState.kind === ThirdPartyTypeEnum.Person && (
          <PersonForm
            formVariant={FormVariant.Creation}
            initialValues={personInitialValues}
            isInternal={!!isInternal}
            onCancel={blockableCloseDrawer}
            onSuccess={handleSuccess}
            setMustBlockAction={setMustBlockAction}
            showCancelButton
            title="Formulaire de création d'une Personne"
          />
        )}
        {drawerState.kind === ThirdPartyTypeEnum.Organization &&
          drawerState.mode === 'create' && (
            <OrganizationForm
              formVariant={FormVariant.Creation}
              initialValues={organizationInitialValues}
              isInternal={!!isInternal}
              onCancel={blockableCloseDrawer}
              onSuccess={handleSuccess}
              setMustBlockAction={setMustBlockAction}
              showCancelButton
              title="Formulaire de création d'une Organisation"
            />
          )}
        {drawerState.kind === ThirdPartyTypeEnum.Organization &&
          drawerState.mode === 'search' && <OrganizationSearchPanel />}
      </RightDrawer>
    </>
  );
};
